/**
 * Phase 3F: Hook for Final CPT Sequencing
 * 
 * Manages the sequenced CPT lines for export:
 * - Builds lines from billable reconciliation items
 * - Applies modifiers with evidence linking
 * - Handles overrides with audit logging
 * - Prepares export packages
 */

import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useModifierSequencing, type ModifierApplicationRecord } from '@/hooks/useModifierSequencing';
import {
  buildSequencedCptLines,
  type BillableReconciliationItem,
  type SequencedCptLine,
  type SequencingResult,
  type SequencingOptions,
} from '@/lib/phase3/cptSequencingEngine';
import type { SiteOfService, Laterality } from '@/lib/modifiers/types';
import type { EvidenceSnippet } from '@/lib/phase3/types';

export interface FinalCptLine {
  id: string;
  authorizationId: string;
  cptCode: string;
  sequence: number;
  modifiers: string[];
  units: number;
  finalCodeString: string;
  baseReimbursement: number;
  effectiveReimbursement: number;
  modifierExplanations: Record<string, string>;
  isPrimary: boolean;
  reviewRequired: boolean;
  reviewReasons: string[];
  isOverridden: boolean;
  overrideBy?: string;
  overrideAt?: string;
  overrideReason?: string;
  originalModifiers?: string[];
  isDemoEntry: boolean;
}

interface OverrideRequest {
  lineId: string;
  cptCode: string;
  newModifiers: string[];
  reason: string;
}

interface UseFinalCptSequencingOptions {
  authorizationId: string | null;
  billableItems?: BillableReconciliationItem[];
  siteOfService?: SiteOfService;
  payerId?: string;
  planId?: string;
  onExportReady?: () => void;
}

interface UseFinalCptSequencingReturn {
  sequencingResult: SequencingResult | null;
  finalLines: FinalCptLine[];
  loading: boolean;
  error: string | null;
  canExport: boolean;
  totalReimbursement: number;
  // Actions
  buildSequence: (items: BillableReconciliationItem[]) => void;
  overrideModifiers: (request: OverrideRequest) => Promise<boolean>;
  revertOverride: (lineId: string, reason: string) => Promise<boolean>;
  acknowledgeWarning: (lineId: string) => void;
  refreshSequence: () => Promise<void>;
  // Modifier sequencing integration
  modifierApplications: ModifierApplicationRecord[];
  fetchModifierApplications: () => Promise<void>;
}

export function useFinalCptSequencing({
  authorizationId,
  billableItems = [],
  siteOfService = 'hospital',
  payerId,
  planId,
  onExportReady,
}: UseFinalCptSequencingOptions): UseFinalCptSequencingReturn {
  const { isDemoMode } = useDemoMode();
  const [sequencingResult, setSequencingResult] = useState<SequencingResult | null>(null);
  const [finalLines, setFinalLines] = useState<FinalCptLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState<Set<string>>(new Set());

  // Use modifier sequencing hook for persistence
  const {
    applications: modifierApplications,
    fetchApplications: fetchModifierApplications,
    overrideModifiers: overrideModifiersDb,
    revertOverride: revertOverrideDb,
    autoApplyAll,
  } = useModifierSequencing();

  // Build sequence from billable items
  const buildSequence = useCallback((items: BillableReconciliationItem[]) => {
    if (items.length === 0) {
      setSequencingResult(null);
      setFinalLines([]);
      return;
    }

    const options: SequencingOptions = {
      siteOfService,
      payerId,
      planId,
      isDemoMode,
      applyMultipleProcedureReduction: true,
    };

    const result = buildSequencedCptLines(items, options);
    setSequencingResult(result);

    // Convert to FinalCptLine format
    const lines: FinalCptLine[] = result.lines.map((line, index) => ({
      id: `seq-${authorizationId}-${line.cptCode}-${index}`,
      authorizationId: authorizationId || '',
      cptCode: line.cptCode,
      sequence: line.sequence,
      modifiers: line.modifiers,
      units: line.units,
      finalCodeString: line.finalCodeString,
      baseReimbursement: line.baseReimbursement,
      effectiveReimbursement: line.effectiveReimbursement,
      modifierExplanations: line.modifierApplications.reduce((acc, app) => {
        acc[app.modifier] = app.reason;
        return acc;
      }, {} as Record<string, string>),
      isPrimary: line.isPrimary,
      reviewRequired: line.reviewRequired,
      reviewReasons: line.reviewReasons,
      isOverridden: false,
      isDemoEntry: isDemoMode,
    }));

    setFinalLines(lines);

    // Check if ready for export
    if (!result.hasBlockingIssues) {
      onExportReady?.();
    }
  }, [authorizationId, siteOfService, payerId, planId, isDemoMode, onExportReady]);

  // Auto-build when billable items change
  useEffect(() => {
    if (billableItems.length > 0) {
      buildSequence(billableItems);
    }
  }, [billableItems, buildSequence]);

  // Fetch modifier applications from database
  const fetchModifierApps = useCallback(async () => {
    if (!authorizationId) return;
    await fetchModifierApplications(authorizationId);
  }, [authorizationId, fetchModifierApplications]);

  // Override modifiers for a line
  const overrideModifiers = useCallback(async (request: OverrideRequest): Promise<boolean> => {
    if (!authorizationId) return false;
    setLoading(true);
    setError(null);

    try {
      // Find the matching modifier application
      const application = modifierApplications.find(
        a => a.cpt_code === request.cptCode
      );

      if (!application) {
        // Create new application record
        setError('Modifier application not found. Run sequencing first.');
        return false;
      }

      // Use the modifier sequencing hook to persist override
      const success = await overrideModifiersDb(
        application.id,
        request.newModifiers,
        request.reason,
        'link_ops_user', // TODO: Get actual user ID
        'link_ops'
      );

      if (success) {
        // Update local state
        setFinalLines(prev => prev.map(line => {
          if (line.id === request.lineId) {
            return {
              ...line,
              originalModifiers: line.modifiers,
              modifiers: request.newModifiers,
              isOverridden: true,
              overrideBy: 'link_ops_user',
              overrideAt: new Date().toISOString(),
              overrideReason: request.reason,
            };
          }
          return line;
        }));

        // Log audit entry
        await logAuditEntry('MODIFIER_OVERRIDE', request.cptCode, {
          previous_modifiers: application.applied_modifiers,
          new_modifiers: request.newModifiers,
          reason: request.reason,
        });
      }

      return success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to override modifiers');
      return false;
    } finally {
      setLoading(false);
    }
  }, [authorizationId, modifierApplications, overrideModifiersDb]);

  // Revert an override
  const revertOverride = useCallback(async (lineId: string, reason: string): Promise<boolean> => {
    if (!authorizationId) return false;
    setLoading(true);
    setError(null);

    try {
      const line = finalLines.find(l => l.id === lineId);
      if (!line || !line.isOverridden) {
        setError('No override to revert');
        return false;
      }

      const application = modifierApplications.find(
        a => a.cpt_code === line.cptCode
      );

      if (!application) {
        setError('Modifier application not found');
        return false;
      }

      const success = await revertOverrideDb(
        application.id,
        reason,
        'link_ops_user',
        'link_ops'
      );

      if (success) {
        // Update local state
        setFinalLines(prev => prev.map(l => {
          if (l.id === lineId && l.originalModifiers) {
            return {
              ...l,
              modifiers: l.originalModifiers,
              isOverridden: false,
              overrideBy: undefined,
              overrideAt: undefined,
              overrideReason: undefined,
              originalModifiers: undefined,
            };
          }
          return l;
        }));

        // Log audit entry
        await logAuditEntry('MODIFIER_OVERRIDE_REVERTED', line.cptCode, {
          reason,
        });
      }

      return success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revert override');
      return false;
    } finally {
      setLoading(false);
    }
  }, [authorizationId, finalLines, modifierApplications, revertOverrideDb]);

  // Acknowledge a warning (allows export despite warning)
  const acknowledgeWarning = useCallback((lineId: string) => {
    setAcknowledgedWarnings(prev => new Set(prev).add(lineId));
  }, []);

  // Refresh sequence from database
  const refreshSequence = useCallback(async () => {
    if (!authorizationId) return;
    setLoading(true);
    
    try {
      await fetchModifierApps();
      
      // If we have modifier applications, rebuild from them
      if (modifierApplications.length > 0) {
        // Update final lines from stored applications
        setFinalLines(prev => prev.map(line => {
          const app = modifierApplications.find(a => a.cpt_code === line.cptCode);
          if (app) {
            return {
              ...line,
              modifiers: app.applied_modifiers,
              finalCodeString: app.final_code_string,
              isOverridden: app.is_overridden,
              overrideBy: app.override_by || undefined,
              overrideAt: app.override_at || undefined,
              overrideReason: app.override_reason || undefined,
              originalModifiers: app.original_modifiers,
              modifierExplanations: app.modifier_explanations,
            };
          }
          return line;
        }));
      }
    } finally {
      setLoading(false);
    }
  }, [authorizationId, fetchModifierApps, modifierApplications]);

  // Log audit entry to database
  const logAuditEntry = async (action: string, cptCode: string, details: object) => {
    if (!authorizationId) return;
    
    try {
      await supabase.from('authorization_activity_log').insert({
        authorization_id: authorizationId,
        action,
        actor_id: 'link_ops_user', // TODO: Get actual user
        actor_type: 'link_ops',
        details: {
          cpt_code: cptCode,
          ...details,
        },
      });
    } catch (err) {
      console.error('Failed to log audit entry:', err);
    }
  };

  // Calculate if export is allowed
  const canExport = sequencingResult
    ? !sequencingResult.hasBlockingIssues || 
      (acknowledgedWarnings.size >= sequencingResult.lines.filter(l => l.reviewRequired).length)
    : false;

  const totalReimbursement = sequencingResult?.totalEstimatedReimbursement || 0;

  return {
    sequencingResult,
    finalLines,
    loading,
    error,
    canExport,
    totalReimbursement,
    buildSequence,
    overrideModifiers,
    revertOverride,
    acknowledgeWarning,
    refreshSequence,
    modifierApplications,
    fetchModifierApplications: fetchModifierApps,
  };
}
